/**
 * A forecast, as a TradingView indicator.
 *
 * Members read these forecasts against a chart anyway; this saves them drawing
 * the box by hand. Generated in the browser from what the page already has —
 * there is nothing here the server knows that the page does not, and a round
 * trip to build a string would only add a way for the button to fail.
 *
 * It does two jobs. It draws the forecast — the range as a band across the
 * window, the price the model found, the window shaded — so a glance says
 * whether price went where the model said. And it reads back the four numbers
 * the settle dialog asks for: the price when the window opened, the high and
 * low it traded inside the window, and where it closed. Those have to be looked
 * up somewhere, and the chart is where somebody is already looking.
 *
 * Pine v6. Kept close to the idiom of a script the user had already proved
 * works on their charts — plot/bgcolor/table rather than boxes and lines —
 * because a clever script that does not compile is worth less than a plain one
 * that does.
 */

import { displayModelName } from '../../../helpers/modelName';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Pine's timestamp(), in UTC, from anything Date can read. */
const pineTimestamp = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `timestamp("UTC", ${d.getUTCFullYear()}, ${d.getUTCMonth() + 1}, `
    + `${d.getUTCDate()}, ${d.getUTCHours()}, ${d.getUTCMinutes()}, ${d.getUTCSeconds()})`;
};

// Anything going inside a Pine string literal. A model label carrying a quote
// would otherwise end the string and break the script on paste.
const pineStr = (s) => String(s == null ? '' : s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

// Shown wherever a figure is missing, so a table keeps its shape whether the
// forecast is open or settled.
const DASH = '\u2014';

/** "2026-08-31 03:36" in UTC, for a heading a reader compares against the axis. */
const utcStamp = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().replace('T', ' ').slice(0, 16);
};

/** A Pine string literal, for a value that is known at generation time. */
const lit = (v) => `"${pineStr(String(v))}"`;

/**
 * Reads the window's open, high, low and close off the chart, and the helpers
 * the table formats with.
 *
 * Both scripts need it and neither can do without it: an open forecast has no
 * recorded outcome, so the only place its figures can come from is the bars
 * themselves.
 */
const WINDOW_TRACKING = `
// ========== What price is doing inside the window ==========
//
// Read off the chart rather than baked in, so an open forecast fills its own
// outcome rows as it goes and they keep up with the last bar.
var float windowHigh  = na
var float windowLow   = na
var float windowOpen  = na
var float windowClose = na
var float priorClose  = na

// Regular session only, and deliberately so.
//
// These figures used to accumulate over every bar the chart happened to show,
// which meant the answer depended on the reader's own extended-hours setting:
// two people opened the same forecast and read two different ranges, and
// neither matched what the site settled it at. Settlement scores the regular
// session, so this does too, and the number on the chart is now the number in
// the record whatever the chart is set to.
//
// session.ismarket is always true on a 24/7 symbol, so crypto is unaffected.
scoring = inWindow and session.ismarket

// The last known price BEFORE the window opened, not this bar's own open. A
// window that opens overnight has its first scoring bar at the next session's
// open, and that bar's own open carries the overnight gap -- which is not the
// price the model was asked about. Taken from the previous regular-session
// close for the same reason as above: close[1] is whatever bar the chart drew
// last, which is not the same thing on every chart.
if scoring and na(windowOpen)
    windowOpen := na(priorClose) ? open : priorClose

if scoring
    windowHigh  := na(windowHigh) ? high : math.max(windowHigh, high)
    windowLow   := na(windowLow)  ? low  : math.min(windowLow, low)
    windowClose := close

if session.ismarket
    priorClose := close

fmt(v) =>
    na(v) ? "${DASH}" : str.tostring(v, "#.####")

pctOf(v, b) =>
    na(v) or na(b) or b == 0 ? "${DASH}" : str.tostring((v - b) / b * 100, "#.#") + "%"

// Says the figures beside it are still moving. Dropped once the window is over,
// at which point they are final whether or not anyone has settled the forecast.
pending = timenow <= endTime ? "  (pending)" : ""
`;

/**
 * The three rows describing what actually happened, as Pine expressions.
 *
 * A settled forecast gets the figures a person recorded -- those are what the
 * score was computed from, and a chart on a different feed disagrees with them
 * in the last decimal. An open one gets them from the chart, marked "(pending)"
 * on the row's name rather than beside every number.
 */
const outcomeRows = (p) => {
  const settledBase = num(p.current_price);
  const aLow = num(p.actual_low);
  const aHigh = num(p.actual_high);
  const aClose = num(p.actual_price);
  const pct = (v) => `${(((v - settledBase) / settledBase) * 100).toFixed(1)}%`;
  const haveWindow = aLow !== null && aHigh !== null;
  const settled = aClose !== null;

  return [
    {
      label: haveWindow ? lit('Actual range') : '"Actual range" + pending',
      value: haveWindow
        ? lit(`${Math.min(aLow, aHigh)} - ${Math.max(aLow, aHigh)}`)
        : 'fmt(windowLow) + " - " + fmt(windowHigh)',
      move: (haveWindow && settledBase)
        ? lit(`${pct(Math.min(aLow, aHigh))} to ${pct(Math.max(aLow, aHigh))}`)
        : 'pctOf(windowLow, windowOpen) + " to " + pctOf(windowHigh, windowOpen)',
    },
    {
      // Never pending: the price the window opened at is settled the moment it
      // opens, whoever has or has not filled the form in.
      label: lit('Price when asked'),
      value: settledBase !== null ? lit(String(settledBase)) : 'fmt(windowOpen)',
      // No percentage: this is the number the others are measured from, and
      // "0.0%" would read as a result rather than as the origin.
      move: lit('baseline'),
    },
    {
      label: settled ? lit('Actual close') : '"Actual close" + pending',
      value: settled ? lit(String(aClose)) : 'fmt(windowClose)',
      move: (settled && settledBase) ? lit(pct(aClose)) : 'pctOf(windowClose, windowOpen)',
    },
  ];
};

/**
 * The table both scripts show, so a reader who has learnt one has learnt both.
 *
 * Two heading rows -- which asset and horizon, and the window -- because a
 * chart carries several indicators and it must be possible to tell which
 * forecast a table belongs to without opening the script. Then one row per
 * model, then what happened.
 *
 * Four columns always, dashed where a figure does not exist yet. A table whose
 * shape changes with its state makes the reader find their place again on every
 * chart.
 */
const tableBlock = (p, symbol, rows, outcome, trailing = []) => {
  const cols = 4;
  const lastCol = cols - 1;
  const headStart = utcStamp(p.create_date ?? p.input_time);
  const headEnd = utcStamp(p.target_date ?? p.valid_until);
  const blanks = (row, color) => Array.from(
    { length: lastCol },
    (_, i) => `    table.cell(fnTable, ${i + 1}, ${row}, "", text_color = ${color}, text_size = size.normal)`,
  ).join('\n');

  const modelCells = rows.map((r, i) => {
    const n = i + 3;   // two heading rows and the column headers sit above
    return `    table.cell(fnTable, 0, ${n}, "${pineStr(r.label)}${r.current && rows.length > 1 ? '  *' : ''}", text_color = color.new(${r.color}, 0), text_size = size.normal, text_halign = text.align_left)
    table.cell(fnTable, 1, ${n}, "${pineStr(`${r.rangeLow} - ${r.rangeHigh}`)}", text_color = color.silver, text_size = size.normal, text_halign = text.align_right)
    table.cell(fnTable, 2, ${n}, "${pineStr(r.move)}", text_color = color.white, text_size = size.normal, text_halign = text.align_right)
    table.cell(fnTable, 3, ${n}, "${r.score === null ? DASH : r.score.toFixed(1)}", text_color = ${r.score === null ? 'color.gray' : (r.score >= 60 ? 'color.green' : 'color.red')}, text_size = size.normal, text_halign = text.align_right)`;
  }).join('\n');

  const outcomeCells = outcome.map((o, i) => {
    const n = rows.length + 3 + i;
    return `
    table.cell(fnTable, 0, ${n}, ${o.label}, text_color = color.yellow, text_size = size.normal, text_halign = text.align_left)
    table.cell(fnTable, 1, ${n}, ${o.value}, text_color = color.yellow, text_size = size.normal, text_halign = text.align_right)
    table.cell(fnTable, 2, ${n}, ${o.move}, text_color = color.yellow, text_size = size.normal, text_halign = text.align_right)
    table.cell(fnTable, 3, ${n}, "", text_color = color.yellow, text_size = size.normal)`;
  }).join('\n');

  // Rows, not a paragraph in one merged cell.
  //
  // A tall multi-line cell gets its text shrunk to fit, so the settlement block
  // rendered smaller and tighter than everything above it and read as a
  // footnote rather than as part of the table. One row each renders at the same
  // size and on the same grid as the model and outcome rows.
  //
  // A row with no value is a heading and spans the width; one with a value sits
  // in the first two columns like the rest.
  const trailingCells = trailing.map((t, i) => {
    const n = rows.length + outcome.length + 3 + i;
    if (!t.value) {
      return `
    table.cell(fnTable, 0, ${n}, ${t.label}, text_color = color.gray, text_size = size.normal, text_halign = text.align_left)
${blanks(n, 'color.gray')}
    table.merge_cells(fnTable, 0, ${n}, ${lastCol}, ${n})`;
    }
    return `
    table.cell(fnTable, 0, ${n}, ${t.label}, text_color = color.silver, text_size = size.normal, text_halign = text.align_left)
    table.cell(fnTable, 1, ${n}, ${t.value}, text_color = color.white, text_size = size.normal, text_halign = text.align_right)
    table.cell(fnTable, 2, ${n}, "", text_color = color.white, text_size = size.normal)
    table.cell(fnTable, 3, ${n}, "", text_color = color.white, text_size = size.normal)`;
  }).join('\n');

  return `var table fnTable = table.new(position.top_right, ${cols}, ${rows.length + outcome.length + trailing.length + 3}, bgcolor = color.new(color.black, 85), border_color = color.gray)

if barstate.islast
    table.cell(fnTable, 0, 0, "${pineStr(symbol)}${p.timeframe ? `  \u00b7  ${pineStr(String(p.timeframe))}` : ''}", text_color = color.white, text_size = size.normal, text_halign = text.align_left)
${blanks(0, 'color.white')}
    table.merge_cells(fnTable, 0, 0, ${lastCol}, 0)

    table.cell(fnTable, 0, 1, "${pineStr(headStart || '')} to ${pineStr(headEnd || '')} UTC", text_color = color.silver, text_size = size.normal, text_halign = text.align_left)
${blanks(1, 'color.silver')}
    table.merge_cells(fnTable, 0, 1, ${lastCol}, 1)

    table.cell(fnTable, 0, 2, "Model", text_color = color.gray, text_size = size.normal, text_halign = text.align_left)
    table.cell(fnTable, 1, 2, "Range", text_color = color.gray, text_size = size.normal, text_halign = text.align_right)
    table.cell(fnTable, 2, 2, "Move", text_color = color.gray, text_size = size.normal, text_halign = text.align_right)
    table.cell(fnTable, 3, 2, "Score", text_color = color.gray, text_size = size.normal, text_halign = text.align_right)
${modelCells}
${outcomeCells}
${trailingCells}
`;
};

/**
 * The four numbers the settlement form asks for, named as the form names them.
 *
 * The table above already carries all four, and this repeats them -- on purpose.
 * Filling the form means reading four labelled values in the form's own order,
 * and doing that from a table means taking the low and the high out of one
 * "110.34 - 115.05" cell and mapping "Actual close" onto an input called
 * "Closing price". Repetition is cheaper than that translation.
 *
 * Always the chart's own figures, even on a settled forecast: this block exists
 * to be typed from, and the chart is what somebody typing is looking at.
 */
const SETTLE_FORM = [
  { label: '"-- for the settlement form (regular session) --"' },
  { label: '"Price when asked"', value: 'fmt(windowOpen)' },
  { label: '"Window low"', value: 'fmt(windowLow)' },
  { label: '"Window high"', value: 'fmt(windowHigh)' },
  { label: '"Closing price"', value: 'fmt(windowClose)' },
];

/**
 * What to call the model on the chart.
 *
 * The model's own answer where it gave one, our catalogue label otherwise.
 *
 * The catalogue label is a menu entry -- "Gemini - Pro" -- and it is
 * deliberately vague about version, which is the one thing a chart read months
 * from now needs to know: it does not say which Gemini answered. The model's
 * self-report does, and it is also the string that reveals an answer filed
 * under the wrong model, which is a thing worth seeing on the chart rather than
 * only on the page.
 *
 * It stays a claim, and an unverified one -- see model_self_report in the
 * parser -- but nothing on this chart is more than a record of what was said.
 *
 * Trimmed, because the name shares a row with three numbers and rides on top of
 * the price action in a box label, and a model that writes a sentence into the
 * field would push the table across the chart. The full string is on the
 * forecast's own page.
 */
const MAX_MODEL_CHARS = 32;
const modelName = (r) => {
  const filed = String(r.model_label || r.model || 'AI').replace(/^user:/, '');
  // Only ever a refinement. A model that names a different family is
  // confabulating -- DeepSeek asked in its own chat window answered "Claude
  // Opus 4.6" -- and a chart is the worst place to repeat that: the title, the
  // box and the table would all attribute the forecast to a model that never
  // saw it, on an image somebody screenshots and shares. See displayModelName.
  const name = displayModelName(filed, r.model_id, r.model_self_report);
  return name.length > MAX_MODEL_CHARS
    ? `${name.slice(0, MAX_MODEL_CHARS - 1).trimEnd()}…`
    : name;
};

/** One model's row, from a prompt_group entry or from the forecast itself. */
const modelRow = (r, fallbackBase, color, current) => {
  const lo = num(r.prediction_low ?? r.range_min);
  const hi = num(r.prediction_high ?? r.range_max);
  if (lo === null || hi === null) return null;
  const from = num(r.model_reported_price) ?? num(fallbackBase);
  const rangeLow = Math.min(lo, hi);
  const rangeHigh = Math.max(lo, hi);
  return {
    label: modelName(r),
    rangeLow,
    rangeHigh,
    move: from
      ? `${(((rangeLow - from) / from) * 100).toFixed(1)}% to ${(((rangeHigh - from) / from) * 100).toFixed(1)}%`
      : DASH,
    // Derived by the API from the range, the baseline and the close, and
    // already discounted for how far the model's own price was out.
    score: num(r.member_score),
    color,
    current,
  };
};

/**
 * @param {object} p  the prediction as the detail page holds it
 * @returns {string|null} null when the forecast lacks what a chart would need
 */
export const buildPineScript = (p) => {
  if (!p) return null;

  // The detail endpoint renames almost everything on the way out: range_min
  // becomes prediction_low, range_max becomes prediction_high, input_time
  // becomes create_date. The listings keep the column names. Reading only one
  // set meant this returned null on the very page it was built for -- and
  // because it hides its own button when it returns null, the mistake looked
  // like a deployment problem rather than a bug.
  const lo = num(p.prediction_low ?? p.range_min);
  const hi = num(p.prediction_high ?? p.range_max);
  // The detail endpoint renames these on the way out -- input_time becomes
  // create_date and valid_until becomes target_date -- while the listings keep
  // the column names. Both are accepted so this works wherever it is handed a
  // forecast, rather than silently returning null and hiding its own button.
  const start = pineTimestamp(p.create_date ?? p.input_time);
  const end = pineTimestamp(p.target_date ?? p.valid_until);
  if (lo === null || hi === null || !start || !end) return null;

  const rangeLow = Math.min(lo, hi);
  const rangeHigh = Math.max(lo, hi);

  // What the model itself said the asset was worth. Absent on older forecasts,
  // and on those the baseline entered at settlement is the next best line to
  // draw; absent both, the script simply omits that plot rather than drawing a
  // line at zero.
  const anchor = num(p.model_reported_price) ?? num(p.current_price);

  // Raw. pineStr is applied once, at each point where one of these is
  // interpolated into a Pine string literal -- escaping here as well produced a
  // doubly-escaped label, and a doubled backslash made the quotes look escaped
  // to a check that was counting them.
  const symbol = String(p.symbol || '');

  // The same shape the comparison script shows, with one model in it, so a
  // reader who has learnt one table has learnt both. Its own score comes from
  // the group -- the detail row does not carry one.
  const mine = (Array.isArray(p.prompt_group) ? p.prompt_group : [])
    .find((r) => Number(r.id) === Number(p.id));
  const row = modelRow(
    {
      ...p,
      ...(mine || {}),
      model_reported_price: p.model_reported_price ?? (mine && mine.model_reported_price),
      // The detail row carries the storage key; only prompt_group rows carry the
      // bare id modelName needs to recognise the "Other model" catch-all.
      model_id: (mine && mine.model_id) || String(p.model || '').replace(/^user:/, ''),
    },
    p.current_price, 'color.blue', true,
  );
  if (!row) return null;

  // One name for the model, everywhere in the script: the title, the header
  // comment, the box label and the table row. It is the version the model gave
  // for itself, falling back to our catalogue label -- see modelName.
  //
  // The title used to keep the catalogue label on the argument that it is how a
  // reader tells one indicator from another on a busy chart. That was the wrong
  // trade: it put "by Grok" in the title above a box reading "Grok 4.5", which
  // is the disagreement a reader has to stop and resolve rather than a
  // distinction that helps them.
  const model = row.label;
  const title = `ForecastNest — ${symbol} ${p.timeframe || ''} by ${model}`.trim();

  return `//@version=6
// ForecastNest forecast #${num(p.id) ?? ''} — ${symbol} by ${model}
// Generated from ${window.location.origin}/prediction/${num(p.id) ?? ''}
//
// Open the ${symbol} chart, paste this into the Pine editor, and Add to chart.
// The shaded band is the forecast window; the green and red lines are the
// forecast range. The table reads back the numbers the settlement form asks
// for once the window has closed. The model is named as it named itself when it
// answered, so the version is the one that actually ran; where it did not say,
// our own label is used.
indicator("${pineStr(title)}", overlay = true)

// ========== The forecast ==========
startTime  = ${start}
endTime    = ${end}
rangeLow   = ${rangeLow}
rangeHigh  = ${rangeHigh}${anchor === null ? '' : `
modelPrice = ${anchor}   // the price the model said it was working from`}

// How much empty chart to leave after the window.
//
// A forecast that has not closed yet sits hard against the price scale, with
// its box half cut off. A script cannot scroll or zoom -- that is the viewer's
// own state -- and drawing objects placed beyond the last bar do NOT extend the
// axis on their own, whatever their colour. What does is a plot with an offset:
// TradingView creates the future bars it needs to draw one, and the chart grows
// to fit.
//
// In bars rather than time, because bars are what the axis is made of and what
// decides how wide the gap looks. Raise it if an open forecast still sits too
// far right; 0 turns it off.
padBars = input.int(40, "Empty bars after the window", minval = 0, maxval = 500,
     tooltip = "Adds space to the right of the chart so an open forecast is not squeezed against the price scale.")

// Fully transparent, and plotting a real number rather than na: the point is to
// make TradingView allocate the bars, not to draw anything.
plot(rangeHigh, title = "Right margin", offset = padBars, color = color.new(color.blue, 100))

inWindow = time >= startTime and time <= endTime

// ========== Draw it ==========
//
// Boxes and lines rather than plot(). A plot only exists where there is a bar,
// and a forecast window is usually still ahead of the last one -- which drew
// the whole forecast as a stub at the right edge, or as nothing at all. Drawing
// objects placed by time extend past the last bar, which is exactly where a
// forecast lives.
var box  fcBox   = na
var line pxLine  = na
var label loLbl  = na
var label hiLbl  = na

if barstate.islast and na(fcBox)
    fcBox := box.new(left = startTime, top = rangeHigh, right = endTime, bottom = rangeLow,
         xloc = xloc.bar_time,
         border_color = color.new(color.blue, 20), border_width = 1,
         bgcolor = color.new(color.blue, 88),
         text = "${pineStr(row.label)}  " + str.tostring(rangeLow, "#.####") + " to " + str.tostring(rangeHigh, "#.####"),
         text_color = color.new(color.blue, 20), text_size = size.small,
         text_halign = text.align_left, text_valign = text.align_top)

    // The bounds again at the right edge, where the price scale is read.
    hiLbl := label.new(x = endTime, y = rangeHigh, xloc = xloc.bar_time,
         text = str.tostring(rangeHigh, "#.####"), style = label.style_label_left,
         color = color.new(color.green, 20), textcolor = color.white, size = size.small)
    loLbl := label.new(x = endTime, y = rangeLow, xloc = xloc.bar_time,
         text = str.tostring(rangeLow, "#.####"), style = label.style_label_left,
         color = color.new(color.red, 20), textcolor = color.white, size = size.small)
${anchor === null ? '' : `
if barstate.islast and na(pxLine)
    pxLine := line.new(x1 = startTime, y1 = modelPrice, x2 = endTime, y2 = modelPrice,
         xloc = xloc.bar_time, color = color.new(color.orange, 0), width = 1,
         style = line.style_dashed)
`}
// Shades the part of the window that has already happened.
bgcolor(inWindow ? color.new(color.blue, 93) : na, title = "Forecast window")

${WINDOW_TRACKING}
${tableBlock(p, symbol, [row], outcomeRows(p), SETTLE_FORM)}`;
};

/**
 * Every model's answer to one question, on one chart.
 *
 * The comparison table on the page says how far apart the models were in
 * percentages; this says it in the shape a trader actually reads. Five boxes
 * over the same window, each in its own colour, show at a glance whether the
 * models agreed on a level and differed on width, or genuinely disagreed about
 * direction \u2014 which the numbers alone make you reconstruct.
 *
 * The percentages are computed here rather than in Pine. Each model's range is
 * measured from the price that model found for itself, which is the rule the
 * rest of the site uses and which the chart has no way to know: it would need
 * five different denominators that exist only in our database.
 */

// Enough distinct colours for a group of five, which is the size every group
// is. Blue is deliberately not first: the single-forecast script uses blue, and
// two scripts on one chart should not look like the same thing twice.
const PALETTE = ['color.orange', 'color.teal', 'color.purple', 'color.fuchsia', 'color.lime', 'color.blue'];

export const buildComparePineScript = (p) => {
  if (!p || !Array.isArray(p.prompt_group) || p.prompt_group.length < 2) return null;

  const start = pineTimestamp(p.create_date ?? p.input_time);
  const end = pineTimestamp(p.target_date ?? p.valid_until);
  if (!start || !end) return null;

  const rows = p.prompt_group
    .map((r, i) => modelRow(r, p.current_price, PALETTE[i % PALETTE.length], Number(r.id) === Number(p.id)))
    .filter(Boolean);
  if (rows.length < 2) return null;

  const symbol = String(p.symbol || '');
  const top = Math.max(...rows.map((r) => r.rangeHigh));

  const boxes = rows.map((r, i) => `    b${i} := box.new(left = startTime, top = ${r.rangeHigh}, right = endTime, bottom = ${r.rangeLow},
         xloc = xloc.bar_time, border_color = color.new(${r.color}, 0), border_width = ${r.current ? 2 : 1},
         bgcolor = color.new(${r.color}, 93),
         text = "${pineStr(r.label)}", text_color = color.new(${r.color}, 0), text_size = size.small,
         text_halign = text.align_left, text_valign = text.align_${i % 2 === 0 ? 'top' : 'bottom'})`).join('\n');

  return `//@version=6
// ForecastNest \u2014 every model's answer to one question
// ${symbol}, ${rows.length} models, from ${window.location.origin}/prediction/${num(p.id) ?? ''}
//
// Open the ${symbol} chart, paste this into the Pine editor, and Add to chart.
// Each box is one model's forecast range over the same window. The percentages
// are each model's range measured from the price that model found for itself.
// Models are named as they named themselves when they answered, so the version
// is the one that actually ran; where a model did not say, our own label is used.
indicator("${pineStr(`ForecastNest \u2014 ${symbol} ${p.timeframe || ''}, ${rows.length} models`)}", overlay = true)

startTime = ${start}
endTime   = ${end}

// Room on the right, so an open forecast is not squeezed against the price
// scale. Only a plot with an offset makes TradingView create future bars;
// drawings placed beyond the last bar do not extend the axis on their own.
padBars = input.int(40, "Empty bars after the window", minval = 0, maxval = 500)
plot(${top}, title = "Right margin", offset = padBars, color = color.new(color.blue, 100))

inWindow = time >= startTime and time <= endTime
bgcolor(inWindow ? color.new(color.gray, 93) : na, title = "Forecast window")
${WINDOW_TRACKING}
// ========== One box per model ==========
${rows.map((_, i) => `var box b${i} = na`).join('\n')}

if barstate.islast and na(b0)
${boxes}

// ========== The comparison ==========
${tableBlock(p, symbol, rows, outcomeRows(p))}`;
};

export default buildPineScript;
